import { useEffectExceptFirst } from 'shared/library/use-effect-except-first';
import { useDebouncedValue } from 'shared/library/use-debounced-value';
import { useRouter } from 'shared/library/use-router';
import { AnyObject } from 'shared/@types';
import { useEffect, useRef } from 'react';

function toQueryValue(value: unknown): string | null {
  return value === null || value === undefined || value === ''
    ? null
    : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : null;
}

export function normalizeQuery(query: string): string {
  const params = new URLSearchParams();
  const entries: Array<[string, string]> = [];

  new URLSearchParams(query).forEach((value, key) => {
    entries.push([key, value]);
  });

  entries
    .filter(([key, value]) => key !== '' && value !== '')
    .sort(([aKey, aVal], [bKey, bVal]) =>
      aKey === bKey ? aVal.localeCompare(bVal) : aKey.localeCompare(bKey),
    )
    .forEach(([key, value]) => params.append(key, value));

  return params.toString();
}

export function buildQuery<T extends AnyObject>(filters: T): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(filters).sort(([a], [b]) => a.localeCompare(b))) {
    if (key === 'page' && value === 1) {
      continue;
    }

    if (Array.isArray(value)) {
      const serialized = value
        .map(toQueryValue)
        .filter((item): item is string => item !== null)
        .join(',');

      if (serialized) {
        params.set(key, serialized);
      }

      continue;
    }

    const serialized = toQueryValue(value);
    if (serialized !== null) {
      params.set(key, serialized);
    }
  }

  return normalizeQuery(params.toString());
}

function parseAsPath(asPath: string | undefined): { pathname: string; query: string } {
  const [path = ''] = (asPath ?? '/').split('#');
  const [pathname = '', query = ''] = path.split('?');
  return { pathname: pathname || '/', query };
}

function getCurrentLocation(asPath: string | undefined): { pathname: string; query: string } {
  if (typeof window !== 'undefined' && window.location) {
    return {
      pathname: window.location.pathname,
      query: window.location.search.replace(/^\?/, ''),
    };
  }

  return parseAsPath(asPath);
}

export function useDebouncedQueryFilter<T extends AnyObject>(
  initialFilter: T,
  filter: T,
  onUpdate: (isLocalNavigation: boolean) => void,
  options?: {
    enabled?: boolean;
    pathnameOverride?: string;
    navigate?: (href: string) => void;
    debounceMs?: number;
  },
) {
  const debouncedFilters = useDebouncedValue(filter, options?.debounceMs);
  const { push, asPath, events } = useRouter();
  const pushRef = useRef(push);
  const onUpdateRef = useRef(onUpdate);
  const navigateRef = useRef(options?.navigate);
  navigateRef.current = options?.navigate;
  const currentNormalizedQueryRef = useRef('');
  const isMountedRef = useRef(true);
  const previousInitialQueryRef = useRef<string | null>(null);
  const pendingSyncQueryRef = useRef<string | null>(null);
  const lastPushedQueryRef = useRef<string | null>(null);
  const initialUpdateIsLocalRef = useRef(false);
  const leavingForRef = useRef<string | null>(null);
  const enabled = options?.enabled ?? true;
  const { pathname: detectedPathname, query: currentQuery } = getCurrentLocation(asPath);
  const currentPathname = options?.pathnameOverride ?? detectedPathname;
  const initialQuery = buildQuery(initialFilter);
  const initialQueryRef = useRef(initialQuery);
  initialQueryRef.current = initialQuery;
  const filterQuery = buildQuery(filter);
  const filterQueryRef = useRef(filterQuery);
  filterQueryRef.current = filterQuery;
  const isLocalNavigation = lastPushedQueryRef.current === initialQuery;
  const currentNormalizedQuery = normalizeQuery(currentQuery);

  useEffect(() => {
    pushRef.current = push;
  }, [push]);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    leavingForRef.current = null;
    let samePageNavigation: {
      href: string;
      initialQuery: string;
      filterQuery: string;
      local: boolean;
    } | null = null;
    const onStart = (href: string) => {
      leavingForRef.current = parseAsPath(href).pathname !== detectedPathname ? href : null;
      samePageNavigation =
        leavingForRef.current === null
          ? {
              href,
              initialQuery: initialQueryRef.current,
              filterQuery: filterQueryRef.current,
              local: lastPushedQueryRef.current === normalizeQuery(parseAsPath(href).query),
            }
          : null;
    };
    const onError = (_error: unknown, href?: string) => {
      if (leavingForRef.current === href) leavingForRef.current = null;
      if (samePageNavigation?.href === href) samePageNavigation = null;
    };
    const onComplete = (href: string) => {
      if (samePageNavigation?.href !== href) return;
      const navigation = samePageNavigation;
      samePageNavigation = null;
      if (
        !enabled ||
        navigation.local ||
        navigation.initialQuery !== initialQueryRef.current ||
        navigation.filterQuery !== filterQueryRef.current ||
        filterQueryRef.current === initialQueryRef.current ||
        normalizeQuery(parseAsPath(href).query) !== initialQueryRef.current
      )
        return;

      // Back can cancel a local update after history changes but before its
      // props render. Restoring those same props does not change initialQuery,
      // so the normal prop-change effect cannot restore the edited controls.
      lastPushedQueryRef.current = null;
      pendingSyncQueryRef.current = initialQueryRef.current;
      onUpdateRef.current(false);
    };
    const browserWindow =
      typeof window !== 'undefined' &&
      window.location &&
      typeof window.addEventListener === 'function' &&
      typeof window.removeEventListener === 'function'
        ? window
        : null;
    const onPopState = () => {
      if (
        !enabled ||
        !browserWindow ||
        browserWindow.location.pathname !== detectedPathname ||
        normalizeQuery(browserWindow.location.search) !== initialQueryRef.current ||
        filterQueryRef.current === initialQueryRef.current ||
        (samePageNavigation &&
          (samePageNavigation.initialQuery !== initialQueryRef.current ||
            samePageNavigation.filterQuery !== filterQueryRef.current))
      )
        return;

      // Next can cancel both renders during a quick Back, restoring history
      // without a routeChangeComplete or new props for the cached filter.
      samePageNavigation = null;
      lastPushedQueryRef.current = null;
      pendingSyncQueryRef.current = initialQueryRef.current;
      onUpdateRef.current(false);
    };
    events.on('routeChangeStart', onStart);
    events.on('routeChangeError', onError);
    events.on('routeChangeComplete', onComplete);
    browserWindow?.addEventListener('popstate', onPopState);
    return () => {
      events.off('routeChangeStart', onStart);
      events.off('routeChangeError', onError);
      events.off('routeChangeComplete', onComplete);
      browserWindow?.removeEventListener('popstate', onPopState);
    };
  }, [detectedPathname, events, enabled]);

  useEffect(() => {
    currentNormalizedQueryRef.current = currentNormalizedQuery;
    // Back can abort a local request before its props arrive. Its later Forward target
    // is external history, so it must not inherit that abandoned local marker.
    if (
      lastPushedQueryRef.current !== null &&
      lastPushedQueryRef.current !== currentNormalizedQuery
    ) {
      lastPushedQueryRef.current = null;
    }
  }, [currentNormalizedQuery]);

  useEffect(() => {
    if (previousInitialQueryRef.current === null) {
      previousInitialQueryRef.current = initialQuery;
      return;
    }

    if (previousInitialQueryRef.current !== initialQuery) {
      initialUpdateIsLocalRef.current = lastPushedQueryRef.current === initialQuery;
      // An earlier local navigation must not replace newer checkbox changes.
      pendingSyncQueryRef.current = initialUpdateIsLocalRef.current ? null : initialQuery;
      lastPushedQueryRef.current = null;
      previousInitialQueryRef.current = initialQuery;
    }
  }, [initialQuery]);

  useEffect(() => {
    // A new edit can replace the restored value before its debounce ever emits it.
    // Actual filter synchronization is enough to release the navigation barrier.
    if (pendingSyncQueryRef.current === filterQuery) {
      pendingSyncQueryRef.current = null;
    }
  }, [filterQuery, initialQuery]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffectExceptFirst(() => {
    if (!enabled) {
      return;
    }

    const nextQuery = buildQuery(debouncedFilters);

    // Next keeps this screen mounted while loading a clicked result. A pending
    // filter must not cancel that navigation and send the user back to the index.
    if (leavingForRef.current !== null) return;

    // A timer from an older edit must not overwrite the current controls or route.
    if (nextQuery !== filterQueryRef.current) return;

    if (pendingSyncQueryRef.current !== null) {
      if (nextQuery !== pendingSyncQueryRef.current) {
        return;
      }

      pendingSyncQueryRef.current = null;
    }

    if (nextQuery === initialQuery || nextQuery === currentNormalizedQueryRef.current) {
      return;
    }

    lastPushedQueryRef.current = nextQuery;
    const href = nextQuery ? `${currentPathname}?${nextQuery}` : currentPathname;
    if (navigateRef.current) navigateRef.current(href);
    else pushRef.current(href, undefined);
  }, [debouncedFilters, currentPathname, initialQuery, enabled]);

  useEffectExceptFirst(() => {
    if (!enabled) {
      return;
    }

    if (!isMountedRef.current) {
      return;
    }

    onUpdateRef.current(initialUpdateIsLocalRef.current);
  }, [initialQuery, enabled]);

  return isLocalNavigation;
}
